import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Card,
  CardContent,
  TextField,
  Chip,
  LinearProgress,
  Stack,
  Paper,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  IconButton,
} from '@mui/material';
import {
  AutoAwesome as AIIcon,
  LocalOffer as KeywordsIcon,
  CheckCircle as CheckIcon,
  Close as CloseIcon,
  Edit as EditIcon,
} from '@mui/icons-material';

const ServiceGeneratorWizard = ({
  open,
  onClose,
  selectedBusiness,
  onServicesGenerated,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [keywords, setKeywords] = useState([]);
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [categories, setCategories] = useState([]);  // Array of {name, displayName}
  const [selectedCategories, setSelectedCategories] = useState([]);  // Array of category names (not displayNames)
  const [categoryMap, setCategoryMap] = useState({});  // Map displayName -> name
  const [serviceCount, setServiceCount] = useState(1);
  const [generatedServices, setGeneratedServices] = useState([]);
  const [error, setError] = useState(null);
  const [generatedDescriptions, setGeneratedDescriptions] = useState({});
  const [editingServiceIndex, setEditingServiceIndex] = useState(null);
  const [duplicates, setDuplicates] = useState([]);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [existingServices, setExistingServices] = useState({});

  // Load keywords and categories from business
  useEffect(() => {
    const loadData = async () => {
      if (!selectedBusiness?.id && !selectedBusiness?.googleLocationId) {
        return;
      }

      try {
        const locationId = selectedBusiness.googleLocationId || selectedBusiness.id;

        // Load keywords
        const keywordResponse = await fetch(`/api/businesses/${locationId}/keywords`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        });

        if (keywordResponse.ok) {
          const data = await keywordResponse.json();
          let keywordList = [];

          if (data.success && data.keywords) {
            keywordList = data.keywords;
          } else if (data.data) {
            keywordList = data.data;
          } else if (Array.isArray(data)) {
            keywordList = data;
          }

          // Extract keyword strings from various formats
          const normalizedKeywords = keywordList.map(kw => {
            if (typeof kw === 'string') {
              return kw;
            }
            if (kw.keyword) {
              return kw.keyword;
            }
            if (kw.name) {
              return kw.name;
            }
            return String(kw);
          }).filter(k => k && k.trim().length > 0);

          setKeywords(normalizedKeywords);
          // Auto-select all keywords
          setSelectedKeywords(normalizedKeywords);
        }

        // Load services to get categories
        const servicesResponse = await fetch(`/api/services/${locationId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        });

        if (servicesResponse.ok) {
          const servicesData = await servicesResponse.json();
          console.log('📂 Services data received:', servicesData);

          // Try different paths to get categories
          let serviceCategories = [];

          if (servicesData.data?.categories && Array.isArray(servicesData.data.categories)) {
            serviceCategories = servicesData.data.categories;
          } else if (servicesData.categories && Array.isArray(servicesData.categories)) {
            serviceCategories = servicesData.categories;
          }

          // If no categories found in dedicated field, extract from freeFormServiceItems
          if (serviceCategories.length === 0 && servicesData.data?.freeFormServiceItems) {
            const freeFormItems = servicesData.data.freeFormServiceItems;
            const categorySet = new Set();
            freeFormItems.forEach(item => {
              if (item.label?.displayName) {
                // Try to get category from the service data structure
                if (item.category) {
                  const displayName = typeof item.category === 'string'
                    ? item.category
                    : item.category.displayName || item.category.name;
                  categorySet.add(displayName);
                }
              }
            });
            serviceCategories = Array.from(categorySet);
          }

          console.log('📂 Extracted categories:', serviceCategories);

          // Extract unique category display names
          const categoryNames = serviceCategories
            .map(cat => {
              if (typeof cat === 'string') return cat;
              return cat.displayName || cat.name || String(cat);
            })
            .filter((name, idx, arr) => name && arr.indexOf(name) === idx);

          console.log('📂 Extracted categories (full objects):', serviceCategories);

          // Create mapping of displayName -> name for proper category ID
          const newCategoryMap = {};
          const categoryDisplayNames = [];

          serviceCategories.forEach(cat => {
            const name = typeof cat === 'string' ? cat : (cat.name || '');
            const displayName = typeof cat === 'string' ? cat : (cat.displayName || cat.name || '');

            if (displayName && name) {
              newCategoryMap[displayName] = name;
              categoryDisplayNames.push({ name, displayName });
            }
          });

          console.log('📂 Category map:', newCategoryMap);
          console.log('📂 Category display names:', categoryDisplayNames);

          setCategoryMap(newCategoryMap);
          setCategories(categoryDisplayNames);

          // Auto-select all categories (use displayName for selection)
          if (categoryDisplayNames.length > 0) {
            setSelectedCategories(categoryDisplayNames.map(cat => cat.displayName));
          }
        }
      } catch (err) {
        console.error('Error loading data:', err);
      }
    };

    if (open) {
      loadData();
    }
  }, [open, selectedBusiness]);

  // Fetch existing services to check for duplicates
  const fetchExistingServices = useCallback(async () => {
    try {
      const locationId = selectedBusiness.googleLocationId || selectedBusiness.id;
      console.log('📡 Fetching existing services from:', locationId);

      const response = await fetch(`/api/services/${locationId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      console.log('📥 Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('📦 Full response data:', data);

        const serviceData = data.data || {};
        console.log('📋 Service data:', serviceData);

        // Create a map of existing services by category and name
        const existingMap = {};
        if (serviceData.freeFormServiceItems && Array.isArray(serviceData.freeFormServiceItems)) {
          console.log('🔄 Processing', serviceData.freeFormServiceItems.length, 'existing freeForm services');

          serviceData.freeFormServiceItems.forEach((service, idx) => {
            const category = service.category || 'default';
            const displayName = service.label?.displayName || service.displayName || '';

            if (!existingMap[category]) {
              existingMap[category] = [];
            }

            existingMap[category].push({
              name: displayName,
              description: service.label?.description || ''
            });

            console.log(`   Service ${idx}: "${displayName}" in category "${category}"`);
          });
        } else {
          console.warn('⚠️ No freeFormServiceItems found in response');
        }

        setExistingServices(existingMap);
        console.log('✅ Existing services map created:', existingMap);
        return { success: true, data: existingMap }; // Return the map directly
      } else {
        const errorText = await response.text();
        console.error('Failed to fetch existing services:', response.status, errorText);
        setError(`Fehler beim Abrufen bestehender Services: ${response.status}`);
        return { success: false, data: {} };
      }
    } catch (err) {
      console.error('Error fetching existing services:', err);
      setError(`Fehler beim Abrufen bestehender Services: ${err.message}`);
      return { success: false, data: {} };
    }
  }, [selectedBusiness]);

  // Check for duplicate services
  const checkForDuplicates = useCallback((services) => {
    const duplicatesList = [];
    const nonDuplicates = [];

    console.log('🔍 Checking duplicates against existingServices:', existingServices);
    console.log('📋 Services to check:', services.length);

    services.forEach((service, idx) => {
      const category = service.category || 'default';
      const serviceName = (service.displayName || service.name || '').trim().toLowerCase();

      // Check if service name already exists in this category
      const categoryServices = existingServices[category] || [];

      console.log(`  Service ${idx}: "${service.displayName || service.name}" in category "${category}"`);
      console.log(`    Existing in category: ${categoryServices.map(s => s.name).join(', ') || 'none'}`);

      const isDuplicate = categoryServices.some(
        existing => (existing.name || '').trim().toLowerCase() === serviceName
      );

      console.log(`    Is duplicate: ${isDuplicate}`);

      if (isDuplicate) {
        duplicatesList.push({
          index: idx,
          name: service.displayName || service.name,
          category: service.categoryDisplayName || service.category
        });
      } else {
        nonDuplicates.push(service);
      }
    });

    console.log(`✅ Duplicate check complete: ${duplicatesList.length} duplicates, ${nonDuplicates.length} non-duplicates`);
    return { duplicatesList, nonDuplicates };
  }, [existingServices]);

  // Real-time duplicate check when services are edited
  useEffect(() => {
    if (generatedServices.length > 0 && Object.keys(existingServices).length > 0) {
      const { duplicatesList } = checkForDuplicates(generatedServices);
      setDuplicates(duplicatesList);
      console.log(`🔄 Real-time duplicate check: ${duplicatesList.length} duplicates found`);
    }
  }, [generatedServices, existingServices, checkForDuplicates]);

  const handleSelectKeyword = (keyword) => {
    setSelectedKeywords(prev =>
      prev.includes(keyword)
        ? prev.filter(k => k !== keyword)
        : [...prev, keyword]
    );
  };

  const handleSelectCategory = (category) => {
    setSelectedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const handleGenerateServices = async () => {
    console.log('🚀 Generate Services clicked!');
    console.log('selectedKeywords:', selectedKeywords);
    console.log('selectedCategories:', selectedCategories);
    console.log('serviceCount value:', serviceCount, 'type:', typeof serviceCount);

    if (selectedKeywords.length === 0) {
      setError('Bitte wählen Sie mindestens ein Keyword aus');
      console.error('❌ No keywords selected');
      return;
    }

    if (selectedCategories.length === 0) {
      setError('Bitte wählen Sie mindestens eine Kategorie aus');
      console.error('❌ No categories selected');
      return;
    }

    if (serviceCount < 1 || serviceCount > 10) {
      setError('Bitte geben Sie eine Anzahl zwischen 1 und 10 ein');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const locationId = selectedBusiness.googleLocationId || selectedBusiness.id;
      const totalServicesToGenerate = selectedCategories.length * selectedKeywords.length * serviceCount;

      console.log(`📊 GENERATION SETTINGS:`);
      console.log(`   Categories: ${selectedCategories.length} (${selectedCategories.join(', ')})`);
      console.log(`   Keywords: ${selectedKeywords.length} (${selectedKeywords.join(', ')})`);
      console.log(`   Services per keyword: ${serviceCount}`);
      console.log(`   Total to generate: ${totalServicesToGenerate}`);
      console.log(`🤖 Generating ${totalServicesToGenerate} services`);
      console.log(`📍 Location ID: ${locationId}`);

      // 🔥 FIRST: Fetch existing services BEFORE generation
      console.log('📋 Fetching existing services BEFORE generation...');
      const fetchResult = await fetchExistingServices();
      if (!fetchResult.success) {
        throw new Error('Failed to fetch existing services');
      }
      const existingServicesMap = fetchResult.data;
      console.log('✅ Existing services loaded:', existingServicesMap);

      // Generate services for each selected category with retry logic
      const allGeneratedServices = [];
      const targetTotalServices = selectedCategories.length * selectedKeywords.length * serviceCount;

      console.log(`\n🔄 CATEGORY LOOP START`);
      console.log(`   Total categories to process: ${selectedCategories.length}`);
      console.log(`   Keywords per category: ${selectedKeywords.length}`);
      console.log(`   Services per keyword: ${serviceCount}`);
      console.log(`   Target per category: ${selectedKeywords.length * serviceCount}`);
      console.log(`   Expected total: ${targetTotalServices}\n`);

      for (const categoryDisplayName of selectedCategories) {
        // Convert displayName to category name using the map
        const categoryName = categoryMap[categoryDisplayName] || categoryDisplayName;

        console.log(`\n📂 [Category ${selectedCategories.indexOf(categoryDisplayName) + 1}/${selectedCategories.length}] Generating services for: ${categoryDisplayName} (name: ${categoryName})`);

        // Get existing service names in this category to avoid duplicates
        const existingServicesInCategory = existingServicesMap[categoryName] || [];
        const existingServiceNames = existingServicesInCategory.map(s => s.name).filter(Boolean);
        console.log(`📍 Existing services in "${categoryName}": ${existingServiceNames.length} services`);
        if (existingServiceNames.length > 0) {
          console.log(`   Existing: ${existingServiceNames.slice(0, 5).join(', ')}${existingServiceNames.length > 5 ? `... (${existingServiceNames.length} total)` : ''}`);
        }

        // Collect all unique services for this category (with retry logic)
        // IMPORTANT: targetCount is PER CATEGORY
        const categoryUniqueServices = [];
        const categorySeenNames = new Set(existingServiceNames.map(s => s.toLowerCase().trim()));
        const targetCount = selectedKeywords.length * serviceCount;
        let retryCount = 0;
        const maxRetries = 5;

        console.log(`   🎯 Target per category: ${targetCount} services (${selectedKeywords.length} keywords × ${serviceCount} factor)`);

        while (categoryUniqueServices.length < targetCount && retryCount < maxRetries) {
          // Request more services than needed to account for potential duplicates with existing services
          const stillNeeded = targetCount - categoryUniqueServices.length;
          const requestCount = Math.ceil(stillNeeded * 2); // Request 2x to handle filtering

          const requestBody = {
            keywords: selectedKeywords,
            count: targetCount,  // Send target count, not requested count
            category: categoryName,
            businessName: selectedBusiness.businessName,
            businessType: selectedBusiness.businessType,
            city: selectedBusiness.address?.city || '',
            existingServices: Array.from(categorySeenNames)  // Avoid existing services + previously generated
          };

          console.log(`📤 Attempt ${retryCount + 1}/${maxRetries}:`);
          console.log(`   Existing services passed: ${existingServiceNames.length}`);
          console.log(`   Previously generated: ${categoryUniqueServices.length}`);
          console.log(`   Still need: ${stillNeeded}`);
          console.log(`   Requesting: ${requestCount} services (${stillNeeded} × 2 for filtering)`);
          console.log(`   Total to avoid: ${categorySeenNames.size} services (existing + generated)`);


          const response = await fetch(`/api/services/${locationId}/generate`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestBody)
          });

          console.log('📥 Response status:', response.status);

          if (!response.ok) {
            const errorData = await response.json();
            console.error('❌ API Error:', errorData);
            throw new Error(errorData.message || `Failed to generate services for category: ${categoryDisplayName}`);
          }

          const data = await response.json();
          const generatedServices = data.services || [];
          console.log(`📦 Received ${generatedServices.length} services (attempt ${retryCount + 1})`);

          // Add only NEW services (not seen in category yet, and not existing)
          let addedCount = 0;
          generatedServices.forEach((service) => {
            const serviceName = (service.displayName || service.name || '').trim().toLowerCase();

            // Only add if it's new AND we still need more services
            if (!categorySeenNames.has(serviceName) && categoryUniqueServices.length < targetCount) {
              categorySeenNames.add(serviceName);
              categoryUniqueServices.push({
                ...service,
                category: categoryName,
                categoryDisplayName: categoryDisplayName,
                _tempId: `${categoryName}-${categoryUniqueServices.length}-${Date.now()}`,
                generatedDescription: ''
              });
              addedCount++;
            }
          });

          console.log(`   ✅ Added ${addedCount} new | Total in category: ${categoryUniqueServices.length}/${targetCount} | Retry: ${retryCount + 1}/${maxRetries}`);
          retryCount++;
        }

        if (categoryUniqueServices.length < targetCount) {
          console.error(`❌ FAILED: Only generated ${categoryUniqueServices.length}/${targetCount} unique services for "${categoryDisplayName}" after ${retryCount} attempts`);
          throw new Error(`Could not generate ${targetCount} unique services for category "${categoryDisplayName}". Only got ${categoryUniqueServices.length}. Please try again.`);
        } else {
          console.log(`✅ SUCCESS: Generated exactly ${targetCount} unique services for category "${categoryDisplayName}"`);
        }

        allGeneratedServices.push(...categoryUniqueServices);
        console.log(`✅ Added ${categoryUniqueServices.length} services for "${categoryDisplayName}" to allGeneratedServices`);
        console.log(`   Running total: ${allGeneratedServices.length}/${targetTotalServices}\n`);
      }

      console.log(`🔄 CATEGORY LOOP END\n`);

      // Verify final count matches expected
      let finalServices = allGeneratedServices;
      const finalUniqueCount = finalServices.length;
      console.log(`\n✅ FINAL RESULT: ${finalUniqueCount} unique services generated (expected: ${targetTotalServices})`);

      if (finalUniqueCount !== targetTotalServices) {
        console.warn(`⚠️ Count mismatch! Expected ${targetTotalServices}, got ${finalUniqueCount}`);

        if (finalUniqueCount > targetTotalServices) {
          // Truncate excess services - keep only expected count
          const excessCount = finalUniqueCount - targetTotalServices;
          finalServices = finalServices.slice(0, targetTotalServices);
          console.warn(`❌ Generated ${excessCount} more services than expected. Truncating to ${targetTotalServices}`);
        } else {
          // Not enough services - throw error
          throw new Error(`Service count mismatch. Expected ${targetTotalServices}, got ${finalUniqueCount}`);
        }
      }

      setGeneratedServices(finalServices);

      // Generate descriptions for each service (use finalServices, not allGeneratedServices)
      const updatedServices = [...finalServices];
      for (let i = 0; i < updatedServices.length; i++) {
        const service = updatedServices[i];
        try {
          const descResponse = await fetch(`/api/services/${locationId}/generate-description`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              serviceName: service.displayName || service.name
            })
          });

          if (descResponse.ok) {
            const descData = await descResponse.json();
            updatedServices[i].generatedDescription = descData.description || '';
          }
        } catch (err) {
          console.error('Error generating description:', err);
        }
      }

      setGeneratedServices(updatedServices);

      // ✅ Validate final count matches expected
      if (updatedServices.length !== targetTotalServices) {
        console.error(`❌ CRITICAL: Generated ${updatedServices.length} services but expected ${targetTotalServices}`);
        console.error(`   Expected: ${selectedCategories.length} categories × ${selectedKeywords.length} keywords × ${serviceCount} per keyword = ${targetTotalServices}`);
        console.error(`   Actual: ${updatedServices.length} services`);
        throw new Error(`Service count mismatch after truncation. Expected ${targetTotalServices}, got ${updatedServices.length}`);
      } else {
        console.log(`✅ Service count verified: ${updatedServices.length} services generated (expected: ${targetTotalServices})`);
      }

      // ✅ All services generated are already unique and don't conflict with existing services
      // No need for additional duplicate checking - proceed directly to review
      console.log('✅ All services verified as unique. Proceeding to review step...');
      setActiveStep(1); // Move to review step (step 1, since 0-indexed)
    } catch (err) {
      console.error('Error generating services:', err);
      setError(err.message || 'Error generating services');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptServices = async () => {
    try {
      setLoading(true);
      setError(null);

      const locationId = selectedBusiness.googleLocationId || selectedBusiness.id;

      // Get current duplicates (already checked during generation)
      const { duplicatesList, nonDuplicates } = checkForDuplicates(generatedServices);

      // Use non-duplicates if they exist, otherwise use all services
      const servicesToSave = duplicatesList.length > 0 ? nonDuplicates : generatedServices;

      console.log(`💾 Saving ${servicesToSave.length} services in batch...`);
      console.log(`📊 Skipping ${duplicatesList.length} duplicate services`);

      if (servicesToSave.length === 0) {
        setError('❌ Alle Services sind Duplikate. Keine Services zum Speichern.');
        setLoading(false);
        return;
      }

      // Prepare services in a single batch request with validation
      const createdAt = new Date().toISOString();
      const services = servicesToSave.map((service, idx) => {
        const displayName = service.displayName || service.name || `Service ${idx + 1}`;
        const category = service.category || 'Customized Service';
        const description = service.generatedDescription || service.description || '';

        console.log(`📦 Service ${idx}: "${displayName}" in "${category}"`);
        console.log(`   Description: "${description.substring(0, 50)}..."`);

        return {
          type: 'freeForm',
          createdAt: createdAt,
          serviceData: {
            category: category,
            label: {
              displayName: displayName.trim(),
              description: description.trim()
            }
          }
        };
      });

      const batchPayload = {
        services: services,
        expectedCount: servicesToSave.length
      };

      console.log('📝 Batch request payload:', JSON.stringify(batchPayload, null, 2));
      console.log(`📊 Saving ${servicesToSave.length} services`);
      console.log(`📌 Expected count limit: ${servicesToSave.length}`);

      // Send all services in ONE API call
      const response = await fetch(`/api/services/${locationId}/batch`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(batchPayload)
      });

      if (response.ok) {
        const result = await response.json();
        console.log(`✅ All ${servicesToSave.length} services saved successfully in 1 API call`);
        console.log('Batch response:', result);
      } else {
        let errorMessage = `Failed to save services: ${response.status}`;
        try {
          const errorData = await response.json();
          console.error('❌ Backend Error Response:', errorData);
          errorMessage = errorData.message || errorData.error || errorMessage;

          // Add detailed error info for debugging
          if (errorData.details) {
            errorMessage += `\n\nDetails: ${JSON.stringify(errorData.details)}`;
          }
          if (errorData.errors) {
            errorMessage += `\n\nErrors: ${JSON.stringify(errorData.errors)}`;
          }
        } catch (parseErr) {
          console.error('❌ Failed to parse error response:', parseErr);
          const responseText = await response.text();
          console.error('Raw error response:', responseText);
          errorMessage += `\n\nRaw response: ${responseText}`;
        }
        throw new Error(errorMessage);
      }

      onServicesGenerated();
      handleClose();
    } catch (err) {
      console.error('❌ Error during save:', err);
      setError(`🚨 Fehler beim Speichern:\n\n${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setSelectedKeywords([]);
    setSelectedCategories([]);
    setServiceCount(1);
    setGeneratedServices([]);
    setGeneratedDescriptions({});
    setEditingServiceIndex(null);
    setDuplicates([]);
    setShowDuplicateWarning(false);
    setError(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
    sx: {
      height: '100vh', // Fixed percentage of viewport
      maxHeight: '1000px', // Optional maximum
    }
  }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AIIcon color="primary" />
        Services generieren
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 3 }}>
          <Stepper activeStep={activeStep} orientation="vertical">
            {/* Step 1: Select Keywords */}
            <Step>
              <StepLabel>Keywords auswählen</StepLabel>
              <StepContent>
                <Box sx={{ py: 2 }}>
                  {keywords.length === 0 ? (
                    <Alert severity="info">
                      Keine Keywords gefunden. Bitte fügen Sie zuerst Keywords hinzu.
                    </Alert>
                  ) : (
                    <>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Wählen Sie Keywords aus, für die Services generiert werden sollen:
                      </Typography>
                      <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', mb: 3 }}>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {keywords.map((keyword, idx) => {
                            const keywordString = typeof keyword === 'string' ? keyword : (keyword.keyword || keyword.name || String(keyword));
                            const isSelected = selectedKeywords.includes(keywordString);
                            return (
                              <Chip
                                key={idx}
                                label={keywordString}
                                onClick={() => handleSelectKeyword(keywordString)}
                                color={isSelected ? 'primary' : 'default'}
                                variant={isSelected ? 'filled' : 'outlined'}
                                sx={{ mb: 1 }}
                              />
                            );
                          })}
                        </Stack>
                      </Paper>

                      <Typography variant="body2" color="textSecondary" sx={{ mb: 2, mt: 3 }}>
                        Wählen Sie Kategorien aus, für die Services erstellt werden sollen:
                      </Typography>
                      {categories.length === 0 ? (
                        <Alert severity="warning" sx={{ mb: 3 }}>
                          ⚠️ Keine Kategorien verfügbar. Bitte:
                          <ol style={{ marginTop: '8px', marginBottom: 0 }}>
                            <li>Schließen Sie diese Dialog</li>
                            <li>Erstellen Sie mindestens einen Service (mit "Add New Service" oder manuell)</li>
                            <li>Weisen Sie diesem Service eine Kategorie zu</li>
                            <li>Öffnen Sie dann den Service Generator erneut</li>
                          </ol>
                        </Alert>
                      ) : (
                        <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', mb: 3 }}>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {categories.map((category, idx) => {
                              const displayName = typeof category === 'string' ? category : (category.displayName || category.name);
                              const isSelected = selectedCategories.includes(displayName);
                              return (
                                <Chip
                                  key={idx}
                                  label={displayName}
                                  onClick={() => handleSelectCategory(displayName)}
                                  color={isSelected ? 'success' : 'default'}
                                  variant={isSelected ? 'filled' : 'outlined'}
                                  sx={{ mb: 1 }}
                                />
                              );
                            })}
                          </Stack>
                        </Paper>
                      )}

                      <TextField
                        fullWidth
                        label="Services pro Keyword pro Kategorie"
                        type="number"
                        value={serviceCount}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '') {
                            setServiceCount('');
                            return;
                          }
                          const num = parseInt(val, 10);
                          if (!isNaN(num)) {
                            setServiceCount(Math.max(1, Math.min(10, num)));
                          }
                        }}
                        onBlur={() => {
                          if (serviceCount === '' || isNaN(serviceCount)) {
                            setServiceCount(1);
                          }
                        }}
                        inputProps={{ min: 1, max: 10 }}
                        helperText={`Wie viele Services pro Keyword? (1-10) Gesamt: ${selectedCategories.length} × ${selectedKeywords.length} × ${serviceCount} = ${selectedCategories.length * selectedKeywords.length * serviceCount} Services`}
                        sx={{ mb: 3 }}
                      />

                      {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          {error}
                        </Alert>
                      )}

                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          startIcon={loading ? <CircularProgress size={20} /> : <AIIcon />}
                          onClick={handleGenerateServices}
                          disabled={loading || selectedKeywords.length === 0 || selectedCategories.length === 0}
                          sx={{ bgcolor: '#4CAF50', '&:hover': { bgcolor: '#45A049' } }}
                        >
                          {loading ? 'Generiere...' : 'Services generieren'}
                        </Button>
                      </Stack>
                    </>
                  )}
                </Box>
              </StepContent>
            </Step>

            {/* Step 2: Review and Edit Generated Services */}
            <Step>
              <StepLabel>Services überprüfen und bearbeiten</StepLabel>
              <StepContent>
                <Box sx={{ py: 2 }}>
                  {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CircularProgress size={30} />
                      <Typography>Speichern Services...</Typography>
                    </Box>
                  ) : generatedServices.length === 0 ? (
                    <Alert severity="info">
                      Keine Services generiert. Bitte gehen Sie zurück und versuchen Sie es erneut.
                    </Alert>
                  ) : (
                    <>
                      {error && (
                        <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                          {error}
                        </Alert>
                      )}

                      {duplicates.length > 0 && (
                        <Alert severity="warning" sx={{ mb: 2, backgroundColor: '#fff3e0', border: '2px solid #ffb74d' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#e65100' }}>
                            ⚠️ {duplicates.length} von {generatedServices.length} Services sind Duplikate
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#bf360c', display: 'block', mb: 1 }}>
                            Diese Services existieren bereits und werden nicht gespeichert.
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#d84315', fontWeight: 600 }}>
                            ✅ {generatedServices.length - duplicates.length} neue Services werden gespeichert
                          </Typography>
                        </Alert>
                      )}

                      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        {generatedServices.length} Services wurden generiert. Überprüfen und bearbeiten Sie diese bei Bedarf:
                      </Typography>

                      <List sx={{ mb: 3, maxHeight: 500, overflow: 'auto' }}>
                        {generatedServices.map((service, idx) => (
                          <Card key={idx} sx={{ mb: 2, border: editingServiceIndex === idx ? '2px solid #1976d2' : '1px solid #e0e0e0' }}>
                            <CardContent>
                              {editingServiceIndex === idx ? (
                                // Edit Mode
                                <Stack spacing={2}>
                                  <TextField
                                    fullWidth
                                    label="Service Name"
                                    value={service.displayName || service.name}
                                    onChange={(e) => {
                                      const updated = [...generatedServices];
                                      updated[idx].displayName = e.target.value;
                                      setGeneratedServices(updated);
                                    }}
                                    size="small"
                                  />
                                  <TextField
                                    fullWidth
                                    label="Beschreibung"
                                    multiline
                                    rows={3}
                                    value={service.generatedDescription || ''}
                                    onChange={(e) => {
                                      const updated = [...generatedServices];
                                      updated[idx].generatedDescription = e.target.value;
                                      setGeneratedServices(updated);
                                    }}
                                  />
                                  <Stack direction="row" spacing={1}>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      onClick={() => setEditingServiceIndex(null)}
                                    >
                                      Fertig
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => setEditingServiceIndex(null)}
                                    >
                                      Abbrechen
                                    </Button>
                                  </Stack>
                                </Stack>
                              ) : (
                                // View Mode
                                <>
                                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
                                      {service.displayName || service.name}
                                    </Typography>
                                    {(service.categoryDisplayName || service.category) && (
                                      <Chip
                                        label={service.categoryDisplayName || service.category}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        title={`Category ID: ${service.category}`}
                                      />
                                    )}
                                    {duplicates.some(d => d.name === service.displayName) && (
                                      <Chip
                                        label="❌ Duplikat"
                                        size="small"
                                        color="error"
                                        sx={{
                                          fontWeight: 700,
                                          backgroundColor: '#ffcdd2',
                                          color: '#c62828'
                                        }}
                                      />
                                    )}
                                    {!duplicates.some(d => d.name === service.displayName) && (
                                      <Chip
                                        label="✅ Neu"
                                        size="small"
                                        sx={{
                                          fontWeight: 700,
                                          backgroundColor: '#c8e6c9',
                                          color: '#1b5e20'
                                        }}
                                      />
                                    )}
                                  </Stack>
                                  {service.generatedDescription && (
                                    <>
                                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                                        Generierte Beschreibung:
                                      </Typography>
                                      <Typography variant="body2" sx={{ p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
                                        {service.generatedDescription}
                                      </Typography>
                                    </>
                                  )}
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => setEditingServiceIndex(idx)}
                                  >
                                    Bearbeiten
                                  </Button>
                                </>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </List>

                      <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
                        <Button
                          variant="outlined"
                          onClick={() => setActiveStep(0)}
                          disabled={loading}
                          sx={{ flex: 1 }}
                        >
                          Zurück zur Generierung
                        </Button>
                        <Box sx={{ flex: 1 }}>
                          <Button
                            variant="contained"
                            onClick={handleAcceptServices}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
                            fullWidth
                            sx={{
                              py: 1.5,
                              fontSize: '1rem',
                              fontWeight: 700,
                              backgroundColor: '#4CAF50',
                              '&:hover': {
                                backgroundColor: '#45a049'
                              }
                            }}
                          >
                            {loading ? (
                              <>
                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                Speichern...
                              </>
                            ) : (
                              duplicates.length > 0 ? (
                                `✅ Speichern mit ${generatedServices.length - duplicates.length} neuen Services`
                              ) : (
                                `✅ Alle ${generatedServices.length} Services speichern`
                              )
                            )}
                          </Button>
                          {duplicates.length > 0 && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#ff9800', fontWeight: 600, textAlign: 'center' }}>
                              ({duplicates.length} Duplikate werden übersprungen)
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </>
                  )}
                </Box>
              </StepContent>
            </Step>
          </Stepper>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Schließen
        </Button>
      </DialogActions>

      {/* Duplicate Warning Dialog */}
      <Dialog
        open={showDuplicateWarning}
        onClose={() => setShowDuplicateWarning(false)}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown
        onBackdropClick={() => {}} // Prevent closing by clicking backdrop
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#ff9800', fontSize: '1.3rem', fontWeight: 700 }}>
          ⚠️ Vergleich mit bestehenden Services
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Alert severity="info" sx={{ mb: 3, fontSize: '1rem' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Die folgenden generierten Services wurden mit Ihren bestehenden Services verglichen:
              </Typography>
              <Typography variant="caption">
                ❌ {duplicates.length} Services existieren bereits
                <br />
                ✅ {generatedServices.length - duplicates.length} Services sind neu
              </Typography>
            </Alert>

            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#d32f2f' }}>
              ❌ Doppelte Services ({duplicates.length}):
            </Typography>

            <Box sx={{
              maxHeight: 250,
              overflow: 'auto',
              mb: 3,
              border: '2px solid #ffb74d',
              borderRadius: 1,
              p: 2,
              backgroundColor: '#fff3e0'
            }}>
              {duplicates.length > 0 ? duplicates.map((dup, idx) => (
                <Box key={idx} sx={{ mb: 1.5, pb: 1.5, borderBottom: idx < duplicates.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#d32f2f' }}>
                    {idx + 1}. {dup.name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    📁 Kategorie: <strong>{dup.category}</strong>
                  </Typography>
                </Box>
              )) : (
                <Typography variant="body2" color="textSecondary">Keine Duplikate</Typography>
              )}
            </Box>

            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#2e7d32' }}>
              ✅ Services zum Speichern ({generatedServices.length - duplicates.length}):
            </Typography>

            <Alert severity="success" sx={{ mb: 3, p: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: '#2e7d32', fontSize: '1rem' }}>
                ✅ Diese {generatedServices.length - duplicates.length} Services sind neu:
              </Typography>
              <Box sx={{
                maxHeight: 200,
                overflow: 'auto',
                backgroundColor: '#e8f5e9',
                border: '1px solid #81c784',
                borderRadius: 1,
                p: 1.5
              }}>
                {generatedServices.filter(s => !duplicates.some(d => d.name === s.displayName)).map((service, idx) => (
                  <Typography key={idx} variant="body2" sx={{ mb: 0.5, color: '#1b5e20' }}>
                    • {service.displayName} <Typography variant="caption" sx={{ color: '#558b2f' }}>({service.categoryDisplayName})</Typography>
                  </Typography>
                ))}
              </Box>
            </Alert>

            <Box sx={{ my: 3, p: 2, backgroundColor: '#fff9c4', borderRadius: 1, border: '1px solid #fbc02d' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#f57f17', mb: 1 }}>
                💡 Optionen:
              </Typography>
              <Typography variant="caption" sx={{ color: '#f57f17', display: 'block', lineHeight: 1.6 }}>
                • Speichern Sie nur die {generatedServices.length - duplicates.length} neuen Services jetzt<br/>
                • Bearbeiten Sie die Services und versuchen Sie dann erneut zu speichern<br/>
                • Oder bearbeiten Sie die doppelten Services manuell im Service Manager
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setShowDuplicateWarning(false);
              setGeneratedServices([]); // Clear generated services
              setDuplicates([]);
              setActiveStep(0); // Go back to generation step
            }}
            disabled={loading}
            variant="outlined"
            sx={{ flex: 1 }}
          >
            Zurück zur Generierung
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => {
              console.log('✅ User confirmed duplicates, proceeding to review step');
              setShowDuplicateWarning(false);
              setActiveStep(1); // Go to review/edit step
            }}
            disabled={loading}
            startIcon={<CheckIcon />}
            sx={{ flex: 1, fontSize: '1rem', fontWeight: 600 }}
          >
            Fortfahren zur Überprüfung
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ServiceGeneratorWizard;
